// ============================================================================
// Live-sync hook — opens the trainer-scoped /api/ws WebSocket and turns every
// `{type:'changed', entity, id, action}` event into a precise React Query
// cache invalidation, so a change made on ANOTHER device updates this UI
// without a refresh.
// ----------------------------------------------------------------------------
// The originating device is already instant via optimistic mutation updates
// (see the per-domain hooks); this hook handles the cross-device case. The
// entity→keys map lives in api/keys.ts (ENTITY_INVALIDATIONS) so the mapping
// stays beside the keys themselves.
//
// Behaviour:
//   • only connects while authenticated (caller passes `enabled`)
//   • auth + scoping are enforced server-side via the shared vf_session cookie;
//     an unauthenticated socket is closed 1008 before accept (we just retry)
//   • auto-reconnects with capped exponential backoff on unexpected close
//   • cleans up on unmount / when disabled
// ============================================================================
import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ENTITY_INVALIDATIONS } from "../api";

/** The compact change event the backend broadcasts on every mutation. */
interface ChangedEvent {
  type: "changed";
  entity: string;
  id: number | null;
  action: "created" | "updated" | "deleted" | string;
}

function wsUrl(): string {
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  // Same-origin; Vite proxies /api/ws to the FastAPI backend in dev.
  return `${proto}//${window.location.host}/api/ws`;
}

export function useLiveSync(enabled: boolean): void {
  const qc = useQueryClient();
  // Keep the latest client without re-running the effect on every render.
  const qcRef = useRef(qc);
  qcRef.current = qc;

  useEffect(() => {
    if (!enabled) return;

    let socket: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let attempts = 0;
    let closed = false; // set on cleanup so we stop reconnecting
    let wasConnected = false; // true once a socket has opened at least once

    const connect = () => {
      if (closed) return;
      socket = new WebSocket(wsUrl());

      socket.onopen = () => {
        // On a RECONNECT (we'd opened before), the WS only pushes changes while
        // connected — anything mutated during the offline gap (sleep, network
        // blip, backoff window) was missed. Invalidate everything so the UI
        // refetches and converges instead of showing stale clients/sessions/
        // calendar. The first connect needs no sweep (queries load normally).
        if (wasConnected) {
          void qcRef.current.invalidateQueries();
        }
        wasConnected = true;
        attempts = 0;
      };

      socket.onmessage = (ev) => {
        let msg: unknown;
        try {
          msg = JSON.parse(ev.data as string);
        } catch {
          return;
        }
        if (
          !msg ||
          typeof msg !== "object" ||
          (msg as { type?: string }).type !== "changed"
        ) {
          return; // ignore the initial {type:'connected'} greeting + noise
        }
        const { entity } = msg as ChangedEvent;
        const roots = ENTITY_INVALIDATIONS[entity];
        if (!roots) return;
        for (const queryKey of roots) {
          void qcRef.current.invalidateQueries({ queryKey });
        }
      };

      socket.onclose = () => {
        if (closed) return;
        // Capped exponential backoff: 0.5s, 1s, 2s, 4s … max 15s.
        const delay = Math.min(15_000, 500 * 2 ** attempts);
        attempts += 1;
        reconnectTimer = setTimeout(connect, delay);
      };

      socket.onerror = () => {
        // Let onclose drive the reconnect; just close this socket.
        socket?.close();
      };
    };

    connect();

    return () => {
      closed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (socket) {
        socket.onclose = null; // prevent reconnect on intentional close
        socket.close();
      }
    };
  }, [enabled]);
}
